import { z } from 'zod';
/* ==========================================================================
 *  Monetization schemas (ADR-0010)
 *  Browser-safe — no @nutians/db import. Shared between server and mobile.
 * ========================================================================== */
export const planSchema = z.enum(['free', 'premium']);
export const subscriptionStoreSchema = z.enum(['app_store', 'play_store', 'stripe']);
export const premiumFeatureSchema = z.enum([
    'chat_unlimited',
    'advanced_insights',
    'monthly_rollups',
    'full_history',
    'reports_export',
    // ADR-0012 §3
    'best_extraction',
    'ai_summaries',
    'recommendations',
    'configurable_alerts',
    // ADR-0014 §4 / T6.1 — higher daily photo-analysis cap on premium
    'photo_analysis',
    // ADR-0026 / T12.5 — adaptive coaching: weekly expenditure estimate + target proposals
    'adaptive_coaching',
    // T13.3 / ISSUE-076 — full 10-micro panel + gap suggestions (free sees basic 4)
    'nutrient_panel',
    // ADR-0029 / T16.2 — planning: templates, build-rest-of-today, grocery list
    'planning',
    // T16.5 / ISSUE-081 — coach/partner read-only week sharing (B2B bridge)
    'coach_sharing',
]);
/** All features unlocked by premium (ordered). */
export const ALL_PREMIUM_FEATURES = [
    'chat_unlimited',
    'advanced_insights',
    'monthly_rollups',
    'full_history',
    'reports_export',
    'best_extraction',
    'ai_summaries',
    'recommendations',
    'configurable_alerts',
    'photo_analysis',
    'adaptive_coaching',
    'nutrient_panel',
    'planning',
    'coach_sharing',
];
/**
 * Why the trial was blocked when the user is on the free tier.
 *   'eligible'    – verified phone exists and has not yet redeemed a trial.
 *   'needs_phone' – no verified phone on this account.
 *   'redeemed'    – the verified phone already has a trial_redemption row.
 * Null when the user is already on a premium plan (trial eligibility is moot).
 */
export const trialEligibilitySchema = z.enum(['eligible', 'needs_phone', 'redeemed']).nullable();
export const trialOfferVariantSchema = z.enum(['data_ready_14d', 'day0_28d']);
export const trialOfferReasonSchema = z.enum([
    'eligible',
    'coaching_calibrating',
    'needs_phone',
    'redeemed',
    'flagged_ed',
    'already_premium',
]);
/**
 * Server-owned trial timing assignment (ADR-0012 T46.1 revision).
 * Store price, duration, and renewal terms intentionally do not appear here;
 * clients must render those from the selected store product.
 */
export const trialOfferContextSchema = z.object({
    experimentId: z.string().min(1),
    variant: trialOfferVariantSchema,
    offeringIdentifier: z.string().min(1),
    promptNow: z.boolean(),
    reason: trialOfferReasonSchema,
});
/** Response shape for `GET /me/entitlement`. */
export const entitlementSchema = z.object({
    plan: planSchema,
    isPremium: z.boolean(),
    /** True when the user is in their free trial period (isPremium is also true). */
    inTrial: z.boolean(),
    store: subscriptionStoreSchema.nullable(),
    productId: z.string().nullable(),
    /** ISO datetime of next renewal / expiry boundary. Null for free users. */
    currentPeriodEnd: z.string().datetime().nullable(),
    willRenew: z.boolean(),
    inGracePeriod: z.boolean(),
    /** OS subscription manager deep-link (provided by RC). Null when unavailable. */
    managementUrl: z.string().url().nullable(),
    /** Features unlocked. Empty for free tier. */
    features: z.array(premiumFeatureSchema),
    /**
     * Trial eligibility signal (ADR-0018 §2 / ISSUE-063).
     * Null when isPremium=true (already subscribed).
     * Present when isPremium=false so the paywall can show the correct CTA:
     *   'eligible'    → show the current plan/store trial offer
     *   'needs_phone' → "Verify your phone to start your free trial"
     *   'redeemed'    → "Your trial has been used on this phone number"
     */
    trialEligibility: trialEligibilitySchema,
    trialOffer: trialOfferContextSchema,
});
/** T46.3's canonical meaningful-value kinds, reused by the weekly receipt. */
export const trialValueKindSchema = z.enum(['adaptive_coaching', 'planning']);
/** Calm, factual trial recap shown near the real store renewal boundary (T46.3). */
export const trialRecapFactsSchema = z.object({
    loggedDays: z.number().int().nonnegative(),
    weighIns: z.number().int().nonnegative(),
    readinessReady: z.boolean(),
    planningActions: z.number().int().nonnegative(),
    adaptiveReviewed: z.boolean(),
    adaptiveApplied: z.boolean(),
});
export const trialRecapSchema = z.object({
    showNow: z.boolean(),
    trialId: z.string().uuid().nullable(),
    currentPeriodEnd: z.string().datetime().nullable(),
    facts: trialRecapFactsSchema,
    trialOffer: trialOfferContextSchema,
});
/* ---------- Web billing checkout (ADR-0022 / T5.2) ------------------------ */
export const billingIntervalSchema = z.enum(['monthly', 'annual']);
/** Request body for `POST /me/billing/checkout`. */
export const billingCheckoutRequestSchema = z.object({
    /** `pricing_plan.planKey` of the premium plan to purchase. */
    planKey: z.string().min(1),
    /** Billing period to purchase. Defaults to 'monthly' when omitted. */
    interval: billingIntervalSchema.optional(),
});
/** 200 response: a redirect URL to the RC Web Billing hosted checkout. */
export const billingCheckoutResponseSchema = z.object({
    checkoutUrl: z.string().url(),
});
/**
 * 409 response when the user is already premium (the dedupe guard, ADR-0022 §5).
 * `store` is the store the active subscription was bought through, so the UI can
 * route the user kindly (store deep-link for IAP; portal link for an existing web sub).
 */
export const billingCheckoutConflictSchema = z.object({
    error: z.literal('already_premium'),
    store: subscriptionStoreSchema.nullable(),
});
/* ---------- RevenueCat webhook envelope ----------------------------------- */
export const revenueCatEventTypeSchema = z.enum([
    'INITIAL_PURCHASE',
    'RENEWAL',
    'CANCELLATION',
    'EXPIRATION',
    'BILLING_ISSUE',
    'PRODUCT_CHANGE',
    'SUBSCRIPTION_PAUSED',
    'UNCANCELLATION',
    'TRANSFER',
]);
/** The subset of the RC webhook envelope the server validates (ADR-0010 §2). */
export const revenueCatEventSchema = z.object({
    event: z.object({
        id: z.string().min(1),
        type: revenueCatEventTypeSchema,
        app_user_id: z.string().min(1),
        original_app_user_id: z.string().optional(),
        entitlement_ids: z.array(z.string()).optional(),
        product_id: z.string().optional(),
        expiration_at_ms: z.number().nullable().optional(),
        environment: z.string(), // 'SANDBOX' | 'PRODUCTION' (RC sends uppercase)
        store: z.string(), // 'APP_STORE' | 'PLAY_STORE' | 'STRIPE'
        /** RC period type: 'TRIAL' | 'INTRO' | 'NORMAL'. Present on purchase/renewal events. */
        period_type: z.string().optional(),
        /** RC boolean flag set to true during free trial periods. */
        is_trial_period: z.boolean().optional(),
        /** RevenueCat Offering shown for the transaction, when available. */
        presented_offering_id: z.string().optional(),
    }),
    api_version: z.string().optional(),
});
//# sourceMappingURL=monetization.js.map